import { Button, Stack, Typography, Box, Divider } from '@mui/material';
import {
  AttachMoney,
  CalendarToday,
  Home,
  Gavel,
  CheckCircle,
  Description,
  AccessTime,
  AccountBalance,
  Assignment,
} from '@mui/icons-material';
import { listings, offers, UserType, OfferStatus as OS } from '@prisma/client';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { formatCurrency } from '../../../utils/formatCurrency';
import { capitalize, isEmpty } from 'lodash';
import NiceModal from '@ebay/nice-modal-react';
import { useAppDispatch } from '../../../store/store';
import { fetchOffers } from '../../../store/slices/offers';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import BuyerOfferModal from '../../../components/OfferModal/BuyerOfferModal';
import SignBuyerOfferModal from '../../../components/SignBuyerOfferModal/SignBuyerOfferModal';
import Proceeds from '../../Offers/Proceeds';
// eslint-disable-next-line max-len
import { OfferActions } from '../../../components/OfferActions/OfferActions';
import { CounterDecision } from '../../../components/CounterDecision/CounterDecision';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { calculateDaysToClose } from '../../../utils/dateUtils';
import { combineClosingDateTime } from '../../../utils/dateUtils';
import { NegotiationTimeline } from '../../../components/NegotiationTimeline/NegotiationTimeline';
import { useEffect, useState } from 'react';
import offerRoundsService, {
  OfferRoundWithRelations,
} from '../../../api/services/offerRounds';
import closingCostsService, {
  ClosingCostsData,
} from '../../../api/services/closingCosts';
import buyerClosingCostsService from '../../../api/services/buyerClosingCosts';
import { CustomTooltip } from '../../../components/CustomTooltip/CustomTooltip';
import {
  getCurrentTerms,
  getChangedFields,
  isFieldChanged as checkFieldChanged,
  formatFieldValue,
  getCommissionDisplay,
  getCommissionLabel,
  shouldShowOfferStatus,
} from './Offer.helpers';

dayjs.extend(utc);

interface Props {
  offers: offers[];
  listing: listings;
  isPublicView?: boolean;
  token?: string;
  showOfferCreation?: boolean;
  buyerPreview?: boolean;
  readOnly?: boolean;
}

const Offer: React.FC<Props> = ({
  offers,
  listing,
  isPublicView = false,
  token,
  showOfferCreation = false,
  buyerPreview = false,
  readOnly = false,
}) => {
  const dispatch = useAppDispatch();
  const [offerRounds, setOfferRounds] = useState<OfferRoundWithRelations[]>([]);
  // If the counter analysis can't be read, fall back to the plain action row so the
  // buyer is never left without a way to answer the seller.
  const [counterAidUnavailable, setCounterAidUnavailable] = useState(false);
  // A conflict means the server no longer considers this round the buyer's to answer,
  // so the round on screen is stale. Refresh instead of offering actions against it —
  // patching an obsolete round would flip the whole offer to accepted or declined.
  const [staleRoundId, setStaleRoundId] = useState<string | null>(null);
  const [closingCosts, setClosingCosts] = useState<ClosingCostsData | null>(
    null
  );
  const [brokerFee, setBrokerFee] = useState<number | null>(null);

  const dispatchOffers = () => {
    dispatch(fetchOffers());
    // Re-fetch offer rounds after any offer action
    if (offers.length > 0 && offers[0]?.id) {
      offerRoundsService
        .find(offers[0].id, token)
        .then((rounds) => setOfferRounds(rounds))
        .catch((error) => console.error('Error fetching offer rounds:', error));
    }
  };
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useAuth();
  const isBuyer = buyerPreview || (!isPublicView && currentUser?.id === id);
  const isSeller = isPublicView || currentUser?.id === listing.userId;
  const isAdmin =
    !readOnly && !isPublicView && currentUser?.type === UserType.ADMIN;
  const canCounter = isSeller || isAdmin;

  // Fetch offer rounds and closing costs when component mounts or offer changes
  useEffect(() => {
    const fetchData = async () => {
      if (offers.length > 0 && offers[0]?.id) {
        try {
          // Fetch offer rounds
          const rounds = await offerRoundsService.find(offers[0].id, token);
          setOfferRounds(rounds);

          // Fetch closing costs for commission data
          if (isPublicView && listing?.id) {
            const costs = await closingCostsService.find(
              listing.id,
              offers[0].id,
              undefined,
              token
            );
            setClosingCosts(costs);

            // Fetch buyer closing costs to get broker fee that accounts for showings
            try {
              const buyerCosts = await buyerClosingCostsService.find({
                offerId: offers[0].id,
                token,
              });
              // Extract broker fee from the response
              if (buyerCosts?.otherFees?.brokerFee) {
                setBrokerFee(buyerCosts.otherFees.brokerFee);
              }
            } catch (error) {
              console.error('Error fetching buyer closing costs:', error);
            }
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      }
    };

    // Initial fetch
    fetchData();

    // For public view, set up interval to refresh once per minute
    let intervalId: NodeJS.Timeout | null = null;
    if (isPublicView) {
      intervalId = setInterval(() => {
        fetchData();
      }, 60000); // 60000ms = 1 minute
    }

    // Cleanup interval on unmount or when dependencies change
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [offers, token, isPublicView, listing?.id]);

  if (offers.length === 0) {
    return (
      <Stack direction="column" spacing={1}>
        <Typography variant="h6">Offer</Typography>
        {showOfferCreation ? (
          <Button
            variant="contained"
            color="success"
            onClick={() => NiceModal.show(BuyerOfferModal, { listing })}
            sx={{ width: 'fit-content' }}
          >
            Make an Offer
          </Button>
        ) : (
          <Typography>Buyer has not submitted an offer</Typography>
        )}
      </Stack>
    );
  }

  const offer = offers[0];

  // Get the latest round (highest round number)
  const latestRound =
    offerRounds && offerRounds.length > 0 ? offerRounds[0] : null;

  // Use helper functions from Offer.helpers.tsx
  const currentTerms = getCurrentTerms(offer, latestRound);
  const changedFields = getChangedFields(latestRound);
  const isFieldChanged = (fieldName: string) =>
    checkFieldChanged(fieldName, changedFields);
  const formatOriginalValue = (fieldName: string, value: unknown) =>
    formatFieldValue(fieldName, value);

  // Render field with change indicator if modified
  const renderField = (
    label: string,
    value: React.ReactNode,
    fieldName: string,
    icon?: React.ReactNode
  ) => {
    const changed = isFieldChanged(fieldName);
    const originalValue = (offer as any)[fieldName];

    return (
      <>
        <Box display="flex" alignItems="center" gap={0.5}>
          {icon && (
            <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
          )}
          <Typography variant="body1" fontWeight={700}>
            {label}
          </Typography>
          {changed && (
            <CustomTooltip
              title={`This term was modified in the counter offer (Round ${
                latestRound?.roundNumber || 1
              })`}
              arrow
              placement="top"
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#ff9800',
                  ml: 0.5,
                  cursor: 'help',
                }}
              />
            </CustomTooltip>
          )}
        </Box>
        <Typography variant="body1" sx={{ ml: icon ? 3 : 0 }}>
          {value}
          {changed && (
            <Typography
              variant="caption"
              display="block"
              sx={{ color: 'text.secondary', mt: 0.5 }}
            >
              Was: {formatOriginalValue(fieldName, originalValue)}
            </Typography>
          )}
        </Typography>
      </>
    );
  };

  // Section header component
  const SectionHeader = ({ title }: { title: string }) => (
    <Grid2 xs={12}>
      <Typography
        variant="subtitle2"
        sx={{
          color: 'text.secondary',
          fontWeight: 600,
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          letterSpacing: '0.5px',
          mt: 2,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      <Divider sx={{ mb: 1 }} />
    </Grid2>
  );

  const { closingDate } = currentTerms;
  const daysToClose = calculateDaysToClose(combineClosingDateTime(closingDate));

  return (
    <>
      <Grid2 container spacing={1}>
        <Grid2 xs={12}>
          <Typography variant="h6">Offer Details</Typography>
        </Grid2>

        {/* Offer Status Section - Hide in public view */}
        {shouldShowOfferStatus(isPublicView) && (
          <>
            <SectionHeader title="Offer Status" />
            <Grid2 xs={12} sm={3}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <CheckCircle
                  fontSize="small"
                  sx={{ color: 'text.secondary' }}
                />
                <Typography variant="body1" fontWeight={700}>
                  Status
                </Typography>
              </Box>
              <Box
                sx={{ ml: 3, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor:
                      offer.status === 'ACCEPTED'
                        ? '#4caf50'
                        : offer.status === 'DECLINED'
                        ? '#f44336'
                        : offer.status === 'COUNTERED'
                        ? '#2196f3'
                        : offer.status === 'PENDING'
                        ? '#ff9800'
                        : '#757575',
                  }}
                />
                <Typography variant="body1">
                  {capitalize(offer.status)}
                </Typography>
              </Box>
            </Grid2>
            <Grid2 xs={12} sm={3}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <AccessTime fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body1" fontWeight={700}>
                  Submitted
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ ml: 3 }}>
                {new Date(offer.createdAt).toLocaleString(undefined, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </Typography>
            </Grid2>
          </>
        )}

        {/* Offer Expiration - Show in all views including offer portal */}
        <Grid2 xs={12} sm={3}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <AccessTime fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body1" fontWeight={700}>
              Expires On
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ ml: 3 }}>
            {new Date(offer.expiresOn).toLocaleString(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </Typography>
        </Grid2>

        {/* Financial Terms Section */}
        <SectionHeader title="Financial Terms" />
        <Grid2 xs={12} sm={3}>
          {renderField(
            'Price',
            formatCurrency(currentTerms.price),
            'price',
            <AttachMoney fontSize="small" />
          )}
        </Grid2>
        <Grid2 xs={12} sm={3}>
          {renderField(
            'Payment Method',
            capitalize(currentTerms.paymentMethod),
            'paymentMethod',
            <AccountBalance fontSize="small" />
          )}
        </Grid2>
        <Grid2 xs={12} sm={3}>
          {renderField(
            'Earnest Money',
            formatCurrency(currentTerms.earnestMoney),
            'earnestMoney',
            <AttachMoney fontSize="small" />
          )}
        </Grid2>
        {(isFieldChanged('loan') || currentTerms.loan > 0) && (
          <Grid2 xs={12} sm={3}>
            {renderField(
              'Loan Amount',
              formatCurrency(currentTerms.loan),
              'loan',
              <AccountBalance fontSize="small" />
            )}
          </Grid2>
        )}

        {/* Timeline Section */}
        <SectionHeader title="Timeline & Dates" />
        <Grid2 xs={12} sm={3}>
          {renderField(
            'Closing Date',
            dayjs.utc(currentTerms.closingDate).format('MM/DD/YYYY'),
            'closingDate',
            <CalendarToday fontSize="small" />
          )}
        </Grid2>
        <Grid2 xs={12} sm={3}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <AccessTime fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body1" fontWeight={700}>
              Closing In
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ ml: 3 }}>
            {Math.abs(daysToClose)} days
          </Typography>
        </Grid2>
        <Grid2 xs={12} sm={3}>
          {renderField(
            'Inspection Required',
            currentTerms.inspectionPeriod === null ||
              currentTerms.inspectionPeriod === 0
              ? 'No'
              : currentTerms.inspectionPeriod > 0
              ? `Yes, ${currentTerms.inspectionPeriod} day window`
              : `Yes, for information only (${Math.abs(
                  currentTerms.inspectionPeriod
                )} day window)`,
            'inspectionPeriod',
            <Assignment fontSize="small" />
          )}
        </Grid2>

        {/* Contingencies & Concessions Section */}
        <SectionHeader title="Contingencies & Concessions" />
        <Grid2 xs={12} sm={3}>
          {renderField(
            'Concessions',
            currentTerms.concessions === 0
              ? 'None'
              : formatCurrency(currentTerms.concessions),
            'concessions',
            <Gavel fontSize="small" />
          )}
        </Grid2>
        <Grid2 xs={12} sm={3}>
          {renderField(
            'Contingent',
            currentTerms.contingent ? 'Yes' : 'No',
            'contingent',
            <CheckCircle fontSize="small" />
          )}
        </Grid2>
        {(isFieldChanged('appliances') || currentTerms.appliances) && (
          <Grid2 xs={12} sm={3}>
            {renderField(
              'Appliances Included',
              currentTerms.appliances ? 'Yes' : 'No',
              'appliances',
              <Home fontSize="small" />
            )}
          </Grid2>
        )}
        {/* Inspections & Warranties Section - Commented out as these fields are not currently collected */}
        {/* {(isFieldChanged('homeWarrantyType') ||
          currentTerms.homeWarrantyType !== 'NONE' ||
          isFieldChanged('termiteInspection') ||
          currentTerms.termiteInspection ||
          isFieldChanged('termiteBond') ||
          currentTerms.termiteBond ||
          isFieldChanged('sewerInspection') ||
          currentTerms.sewerInspection ||
          isFieldChanged('newSurvey') ||
          currentTerms.newSurvey) && (
          <>
            <SectionHeader title="Inspections & Warranties" />
          </>
        )}
        {(isFieldChanged('homeWarrantyType') ||
          currentTerms.homeWarrantyType !== 'NONE') && (
          <Grid2 xs={12} sm={3}>
            {renderField(
              'Home Warranty',
              currentTerms.homeWarrantyType === 'NONE'
                ? 'None'
                : currentTerms.homeWarrantyType === 'SELLER'
                ? 'Seller Provided'
                : 'Buyer Provided',
              'homeWarrantyType',
              <Build fontSize="small" />
            )}
          </Grid2>
        )}
        {(isFieldChanged('termiteInspection') ||
          currentTerms.termiteInspection) && (
          <Grid2 xs={12} sm={3}>
            {renderField(
              'Termite Inspection',
              currentTerms.termiteInspection ? 'Yes' : 'No',
              'termiteInspection',
              <Build fontSize="small" />
            )}
          </Grid2>
        )}
        {(isFieldChanged('termiteBond') || currentTerms.termiteBond) && (
          <Grid2 xs={12} sm={3}>
            {renderField(
              'Termite Bond',
              currentTerms.termiteBond ? 'Yes' : 'No',
              'termiteBond',
              <Build fontSize="small" />
            )}
          </Grid2>
        )}
        {(isFieldChanged('sewerInspection') ||
          currentTerms.sewerInspection) && (
          <Grid2 xs={12} sm={3}>
            {renderField(
              'Sewer Inspection',
              currentTerms.sewerInspection ? 'Yes' : 'No',
              'sewerInspection',
              <Build fontSize="small" />
            )}
          </Grid2>
        )}
        {(isFieldChanged('newSurvey') || currentTerms.newSurvey) && (
          <Grid2 xs={12} sm={3}>
            {renderField(
              'New Survey',
              currentTerms.newSurvey ? 'Yes' : 'No',
              'newSurvey',
              <Assignment fontSize="small" />
            )}
          </Grid2>
        )} */}

        {/* Commissions & Proceeds Section - Only show for sellers and admins */}
        {!isBuyer && (
          <>
            <SectionHeader title="Commissions & Proceeds" />
            <Grid2 xs={12} sm={3}>
              {renderField(
                getCommissionLabel(isPublicView),
                getCommissionDisplay(
                  isPublicView,
                  closingCosts,
                  currentTerms,
                  brokerFee
                ),
                'bagentComm',
                <AccountBalance fontSize="small" />
              )}
            </Grid2>
            {canCounter && (
              <Grid2 xs={12} sm={4}>
                <Typography variant="body1" fontWeight={700}>
                  Estimated Proceeds
                </Typography>
                <Typography variant="body1">
                  <Proceeds
                    listingId={listing.id}
                    offerId={offer.id}
                    token={token}
                  />
                </Typography>
              </Grid2>
            )}
          </>
        )}

        {/* Additional Terms Section */}
        <SectionHeader title="Additional Terms" />
        <Grid2 xs={12}>
          {renderField(
            'Additional Terms',
            isEmpty(currentTerms.additionalTerms)
              ? 'None'
              : currentTerms.additionalTerms,
            'additionalTerms',
            <Description fontSize="small" />
          )}
        </Grid2>
        {(() => {
          const latestRound =
            offerRounds && offerRounds.length > 0 ? offerRounds[0] : null;

          // The seller has countered and it is the buyer's move: lead with the
          // decision aid rather than three unexplained buttons.
          const ballWithBuyer =
            isBuyer &&
            offer.status === OS.COUNTERED &&
            latestRound?.status === 'PENDING' &&
            latestRound.initiatorType === 'SELLER';

          if (
            readOnly ||
            !ballWithBuyer ||
            !latestRound ||
            counterAidUnavailable ||
            staleRoundId === latestRound.id
          ) {
            return null;
          }

          return (
            <Grid2 xs={12} pt={2}>
              <CounterDecision
                offer={{ ...offer, currentRound: latestRound }}
                listing={listing}
                latestRound={latestRound}
                onActionComplete={() => dispatchOffers()}
                onUnavailable={(reason, roundId) => {
                  if (reason === 'error') {
                    setCounterAidUnavailable(true);
                    return;
                  }
                  if (staleRoundId === roundId) return;
                  setStaleRoundId(roundId);
                  dispatchOffers();
                }}
              />
            </Grid2>
          );
        })()}
        {(() => {
          const latestRound =
            offerRounds && offerRounds.length > 0 ? offerRounds[0] : null;
          const hasActiveNegotiation = latestRound?.status === 'PENDING';

          // The buyer answering a seller counter gets the Counter Decision card
          // above instead of the bare action row.
          const ballWithBuyer =
            isBuyer &&
            offer.status === OS.COUNTERED &&
            latestRound?.status === 'PENDING' &&
            latestRound.initiatorType === 'SELLER';
          const refreshingStaleRound =
            latestRound !== null && staleRoundId === latestRound.id;
          if (
            ballWithBuyer &&
            (!counterAidUnavailable || refreshingStaleRound)
          ) {
            return null;
          }

          // Check if offer is in REVIEWED or SIGNED status
          const isOfferReviewed = offer.status === OS.REVIEWED;
          const isOfferSigned = offer.status === OS.SIGNED;

          // If buyer and offer is REVIEWED, show Review & Sign button
          if (isBuyer && isOfferReviewed && !readOnly) {
            return (
              <Grid2 xs={12} pt={2}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<Description />}
                  onClick={() => {
                    NiceModal.show(SignBuyerOfferModal, {
                      offer: offer,
                      listing: listing,
                    });
                  }}
                  sx={{ color: 'white' }}
                >
                  Review & Sign Offer
                </Button>
              </Grid2>
            );
          }

          // If offer is SIGNED, show status only (but not in public view where actions should still be available)
          if (isOfferSigned && !isPublicView) {
            return (
              <Grid2 xs={12} pt={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircle color="success" />
                  <Typography
                    variant="body1"
                    color="success.main"
                    fontWeight={600}
                  >
                    Offer Signed
                  </Typography>
                </Stack>
              </Grid2>
            );
          }

          // Show actions if:
          // 1. Seller viewing original pending offer
          // 2. Either party viewing active counter-offer from the other party
          // 3. Public view (seller's agent) when offer is SIGNED - seller needs to accept/counter/decline
          // 4. Admin can always act on active offers regardless of whose turn it is
          const showActions =
            (isAdmin &&
              (offer.status === 'PENDING' ||
                (hasActiveNegotiation && offer.status === 'COUNTERED'))) ||
            (canCounter && offer.status === 'PENDING') ||
            (hasActiveNegotiation &&
              offer.status === 'COUNTERED' &&
              ((!canCounter && latestRound.initiatorType === 'SELLER') ||
                (canCounter && latestRound.initiatorType === 'BUYER'))) ||
            (isPublicView && isOfferSigned);

          if (readOnly || !showActions) return null;

          return (
            <Grid2 xs={12} pt={2}>
              <OfferActions
                offer={{
                  ...offer,
                  offerRounds,
                  currentRound: latestRound,
                }}
                onOfferAccepted={() => {
                  dispatchOffers();
                }}
                onActionComplete={() => {
                  dispatchOffers();
                }}
                token={token}
                isPublicView={isPublicView}
                isBuyer={isBuyer}
                isAdmin={isAdmin}
                listing={listing}
              />
            </Grid2>
          );
        })()}

        {/* Negotiation History Section */}
        {offerRounds && offerRounds.length > 0 && (
          <>
            <SectionHeader title="Negotiation History" />
            <Grid2 xs={12}>
              <NegotiationTimeline
                rounds={offerRounds}
                currentUserId={currentUser?.id || ''}
                listingOwnerId={listing.userId}
                isPublicView={isPublicView}
              />
            </Grid2>
          </>
        )}
      </Grid2>
    </>
  );
};

export default Offer;
